(ns ui.view.transaction-process.modals.decision.sections
  "Section render components for the decision modal. Thin: each reads resolved
  data from subs and dispatches events; all derivation lives in sub/event/model."
  (:require
   [common.entity :as entity]
   [common.localization :refer [tr]]
   [common.util :as cu]
   [frontend.state.current-session :as session]
   [re-frame.core :as rf]
   [ui.components.button.view :as button]
   [ui.components.combo-box.view :as combo-box]
   [ui.editors.labelled-input.view :as labelled-input]
   [ui.state.session.sub :as session-sub]
   [ui.util :as uu]
   [ui.view.transaction-process.modals.decision.event :as event]
   [ui.view.transaction-process.modals.decision.model :as model]
   [ui.view.transaction-process.modals.decision.sub :as sub]
   [ui.view.transaction-process.sub :as tp-sub]
   [ui.view.transaction-process.util :as tp-util]))

(defn- field-row
  [{:keys [ctx entity-id field label data-type options value sym-field sym-value container-class mandatory?]}]
  (if (= data-type :data.type/currency-value)
    [labelled-input/currency-view
     {:label          label
      :mandatory?     mandatory?
      :data-type      data-type
      :value          value
      :on-change      (fn [v] (rf/dispatch [::event/update-entity-field ctx entity-id field v]))
      :secondary-args {:data-type :data.type/currency-symbol
                       :value     (or sym-value @session/default-currency)
                       :on-change (fn [v] (rf/dispatch [::event/update-entity-field ctx entity-id sym-field v]))}}]
    [labelled-input/view
     {:label           label
      :mandatory?      mandatory?
      :container-class container-class
      :data-type       data-type
      :value           value
      :options         options
      :on-change       (fn [v] (rf/dispatch [::event/update-entity-field ctx entity-id field v]))}]))

(defn- notes-field-row
  [{:keys [ctx entity-id field label value]}]
  [labelled-input/view
   {:label     label
    :data-type :data.type/text-multiline
    :value     (or value "")
    :on-change (fn [v] (rf/dispatch [::event/update-entity-field ctx entity-id field v]))}])

(defn- resolved-field-row
  "A `field-row` whose label, data-type, and options come from the field's template
  config (the enriched `:template/fields` the read views also consume) rather than
  being hardcoded here — so the edit modal and the read surfaces can never disagree
  about a field's label/type/options. `entity` is the entity the field lives on, used
  for the mandatoriness rule lookup."
  [{:keys [ctx entity-id field field-configs entity container-class]}]
  (let [config  (get field-configs field)
        process @(rf/subscribe [::tp-sub/process ctx])]
    [field-row {:ctx             ctx
                :entity-id       entity-id
                :field           field
                :label           (:field/label config)
                :data-type       (:field/data-type config)
                :options         (:field/options config)
                :container-class container-class
                :mandatory?      (tp-util/field-mandatory? config (merge process entity))
                :value           (get entity field)}]))

(defn details-section
  "All CapMan decision types now share the same Details section: just the
  decision date (meeting type was removed per CapMan testing 2026-05-25)."
  [{:keys [ctx]}]
  (let [decision      @(rf/subscribe [::sub/decision ctx])
        decision-id   @(rf/subscribe [::sub/decision-id ctx])
        field-configs @(rf/subscribe [::sub/field-configs ctx])]
    [:div {:class "flex flex-col gap-4"}
     [resolved-field-row {:ctx             ctx
                          :entity-id       decision-id
                          :field           :decision/date
                          :field-configs   field-configs
                          :entity          decision
                          :container-class "w-56 max-w-full"}]]))

(defn- participant-person-name
  [participant]
  (let [person-ref (:decision-participant/person participant)
        person-id  (when person-ref (or (:db/id person-ref) person-ref))
        person     (when person-id @(rf/subscribe [::session-sub/entity person-id]))]
    (when person
      (entity/display-string person))))

(defn- role-value->ids
  [role-value]
  (cond
    (sequential? role-value) role-value
    (set? role-value)        (vec role-value)
    role-value               [role-value]
    :else                    []))

(defn- resolve-role-selected
  [role-value role-options]
  (let [options-by-id (into {} (map (fn [o] [(:value o) o]) role-options))]
    (mapv (fn [id]
            (or (get options-by-id id)
                (do (tp-util/log-resolve-failure!
                     {:context "Decision participant role not in current options"
                      :value   id})
                    ;; :transaction-process/error-resolving-value already opens
                    ;; with a warning sign - don't prefix a second one.
                    {:value id
                     :label (str (tr :transaction-process/error-resolving-value) ": " (pr-str id))})))
          (role-value->ids role-value))))

(defn- participant-card
  [{:keys [ctx pid participant role-options field-configs]}]
  (let [process @(rf/subscribe [::tp-sub/process ctx])]
    [:div {:class "border border-gray-200 rounded-lg p-3 flex flex-col gap-2"}
     [:div {:class "flex items-center justify-between"}
      [:span {:class "text-md m-0 font-medium text-gray-900 mb-2"}
       (or (participant-person-name participant) (tr :transaction-process/no-person-selected))]
      [:button {:class    "text-xs m-0 text-red-600 hover:underline"
                :on-click #(rf/dispatch [::event/remove-participant ctx pid])}
       (tr :remove)]]
     [resolved-field-row {:ctx           ctx
                          :entity-id     pid
                          :field         :decision-participant/attendance
                          :field-configs field-configs
                          :entity        participant}]
     ;; Role options are tenant-configured named-values, so they are resolved
     ;; from the search index rather than read out of @session/entities: on the
     ;; new grid :entity.type/named-value is not bulk-synced, which left this
     ;; picker with an empty option list and no way to set a mandatory field.
     ;; :editor-type :editor.type/search + :data.type/ref-multi is what routes
     ;; ui.editors.data-input to the open-search editor. The resolved template
     ;; config carries :field/entity-types but drops the :named-value-attribute
     ;; that su/option-field sets, and that key is what scopes the query to this
     ;; field's own options, so it is named explicitly here.
     [labelled-input/view
      {:label                 (get-in field-configs [:decision-participant/role :field/label])
       :mandatory?            (tp-util/field-mandatory? (get field-configs :decision-participant/role) (merge process participant))
       :data-type             :data.type/ref-multi
       :editor-type           :editor.type/search
       :field/entity-types    [:entity.type/named-value]
       :named-value-attribute :decision-participant/role
       :value                 (resolve-role-selected (:decision-participant/role participant) role-options)
       :on-change             (fn [v]
                                (let [ids (if (sequential? v)
                                            (mapv (fn [item] (or (:value item) item)) v)
                                            (when v [(or (:value v) v)]))]
                                  (rf/dispatch [::event/update-entity-field ctx pid :decision-participant/role ids])))}]
     [notes-field-row {:ctx       ctx
                       :entity-id pid
                       :field     :decision-participant/notes
                       :label     (get-in field-configs [:decision-participant/notes :field/label])
                       :value     (:decision-participant/notes participant)}]]))

(defn participants-section
  [{:keys [ctx]}]
  (let [participants    @(rf/subscribe [::sub/participants ctx])
        person-options  @(rf/subscribe [::sub/person-options ctx])
        role-options    @(rf/subscribe [::sub/participant-role-options ctx])
        contact-lists?  @(rf/subscribe [::sub/contact-lists? ctx])
        contact-options @(rf/subscribe [::sub/contact-list-options ctx])
        field-configs   @(rf/subscribe [::sub/field-configs ctx])]
    [:div
     [:div {:class "flex flex-col gap-3"}
      (doall
       (for [[pid participant] participants]
         ^{:key pid}
         [participant-card {:ctx ctx :pid pid :participant participant
                            :role-options role-options :field-configs field-configs}]))]
     [:div {:class (uu/cm "flex flex-wrap items-center gap-2"
                          (when (seq participants) "mt-3"))}
      (when contact-lists?
        [combo-box/view
         {:options                  contact-options
          :on-change                (fn [contact-list-id]
                                      (let [members (-> (session/get-entity contact-list-id)
                                                        :contact-list/members
                                                        vec)]
                                        (rf/dispatch [::event/add-participants ctx members])))
          :search-visible-threshold 10}
         [button/sm-secondary-hollow {}
          [:i {:class "fal fa-plus text-xs m-0"}]
          (tr :add-group)]])
      [combo-box/view
       {:options                  person-options
        :on-change                (fn [person-id] (rf/dispatch [::event/add-participants ctx [person-id]]))
        :search-visible-threshold 10}
       [button/sm-secondary-hollow {}
        [:i {:class "fal fa-plus text-xs m-0"}]
        (tr :transaction-process/add-participant)]]]]))

(defn conditions-section
  [{:keys [ctx]}]
  (let [conditions    @(rf/subscribe [::sub/conditions ctx])
        field-configs @(rf/subscribe [::sub/field-configs ctx])]
    [:div
     [:div {:class "flex flex-col gap-3"}
      (doall
       (for [[cid condition] conditions]
         ^{:key cid}
         [:div {:class "border border-gray-200 rounded-lg p-3 flex flex-col gap-2"}
          [resolved-field-row {:ctx           ctx
                               :entity-id     cid
                               :field         :decision-condition/title
                               :field-configs field-configs
                               :entity        condition}]
          [notes-field-row {:ctx       ctx
                            :entity-id cid
                            :field     :decision-condition/description
                            :label     (get-in field-configs [:decision-condition/description :field/label])
                            :value     (:decision-condition/description condition)}]
          [:button {:class    "self-end text-xs m-0 text-red-600 hover:underline"
                    :on-click #(rf/dispatch [::event/remove-condition ctx cid])}
           (tr :remove)]]))]
     [:div {:class (when (seq conditions) "mt-3")}
      [button/sm-secondary-hollow
       {:on-click #(rf/dispatch [::event/add-condition ctx])}
       [:i {:class "fal fa-plus text-xs m-0"}]
       (tr :transaction-process/add-condition)]]]))

(defn- reservation-node
  "Render a layout node. A vector is a sub-group (its own flex-wrap row that wraps
  as a block within its parent); a map is a resolved field config (leaf)."
  [{:keys [ctx rid reservation process] :as field-ctx} node]
  (if (vector? node)
    (into [:div {:class "flex flex-wrap gap-x-3 gap-y-4" :style (model/reservation-node-style node)}]
          (map-indexed (fn [i child] ^{:key i} (reservation-node field-ctx child)) node))
    (let [{:field/keys [attribute label data-type options]} node]
      [:div {:style (model/reservation-node-style node)}
       (if (= data-type :data.type/text-multiline)
         [notes-field-row {:ctx ctx :entity-id rid :field attribute :label label
                           :value (get reservation attribute)}]
         [field-row {:ctx ctx :entity-id rid :field attribute :label label :data-type data-type
                     :options    options
                     :mandatory? (tp-util/field-mandatory? node (merge process reservation))
                     :value      (get reservation attribute)
                     :sym-field  (cu/currency->symbol attribute)
                     :sym-value  (get reservation (cu/currency->symbol attribute))}])])))

(defn- reservation-card
  [{:keys [ctx rid reservation layout]}]
  (let [type-ref    (:investment-reservation/type reservation)
        type-id     (or (:db/id type-ref) type-ref)
        type-entity (when type-id @(rf/subscribe [::session-sub/entity type-id]))
        type-name   (or (:investment-reservation-type/title type-entity)
                        (when type-entity (entity/display-string type-entity)))
        enabled     (:investment-reservation-type/enabled-fields type-entity)
        enabled?    (fn [attribute] (or (nil? enabled) (contains? (set enabled) attribute)))
        rows        (model/filter-reservation-layout enabled? layout)
        process     @(rf/subscribe [::tp-sub/process ctx])
        field-ctx   {:ctx ctx :rid rid :reservation reservation :process process}]
    [:div {:class "border border-gray-200 rounded-lg p-3"}
     (when type-name
       [:div {:class "text-base m-0 font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200"} type-name])
     (into
      [:div {:class "flex flex-col gap-y-4"}]
      (map-indexed
       (fn [i row]
         ^{:key i}
         (into [:div {:class "flex flex-wrap gap-x-3 gap-y-4"}]
               (map-indexed (fn [j node] ^{:key j} (reservation-node field-ctx node)) row)))
       rows))
     [:div {:class "flex justify-end mt-2"}
      [:button {:class    "text-xs m-0 text-red-600 hover:underline"
                :on-click #(rf/dispatch [::event/remove-reservation ctx rid])}
       (tr :remove)]]]))

(defn reservation-footer
  "The control below the reservation cards: a type picker when the user can read
  any reservation type, otherwise the admin-config hint - but only when the
  section is empty.

  Reading `:entity.type/investment-reservation-type` requires
  `:module/decisions`, so an Investment Team or Portfolio Manager user sees no
  types even on a tenant that has them configured. Reservations already listed
  prove the types exist, which makes the \"ask an admin to add one\" hint
  actively wrong for those roles. DEV-8150."
  [{:keys [ctx available-types reservations]}]
  (cond
    (seq available-types)
    [combo-box/view
     {:options                  (mapv (fn [t]
                                        {:value (:db/id t)
                                         :label (or (:investment-reservation-type/title t)
                                                    (entity/display-string t))})
                                      available-types)
      :on-change                (fn [type-id] (rf/dispatch [::event/add-reservation ctx type-id]))
      :search-visible-threshold 10}
     [button/sm-secondary-hollow {}
      [:i {:class "fal fa-plus text-xs m-0"}]
      (tr :transaction-process/add-reservation)]]

    ;; Surface the admin-config gap explicitly so the user doesn't see the
    ;; combo-box's bare "No options" placeholder and assume the feature is
    ;; broken. CapMan testing 2026-05-21.
    (empty? reservations)
    [:div {:class "rounded border border-dashed border-gray-300 bg-gray-50 px-3 py-2 text-sm m-0 text-gray-600"}
     (tr :transaction-process/no-reservation-types-configured)]))

(defn reservations-section
  [{:keys [ctx]}]
  (let [reservations    @(rf/subscribe [::sub/reservations ctx])
        layout          @(rf/subscribe [::sub/reservation-layout ctx])
        available-types @(rf/subscribe [::sub/available-reservation-types ctx])
        footer          (reservation-footer {:ctx             ctx
                                             :available-types available-types
                                             :reservations    reservations})]
    [:div
     [:div {:class "flex flex-col gap-3"}
      (doall
       (for [[rid reservation] reservations]
         ^{:key rid}
         [reservation-card {:ctx ctx :rid rid :reservation reservation :layout layout}]))]
     (when footer
       [:div {:class (when (seq reservations) "mt-3")}
        footer])]))

(defn notes-section
  [{:keys [ctx]}]
  (let [decision    @(rf/subscribe [::sub/decision ctx])
        decision-id @(rf/subscribe [::sub/decision-id ctx])]
    [:div
     [labelled-input/view
      {:data-type :data.type/text-multiline
       :value     (or (:decision/notes decision) "")
       :on-change (fn [v] (rf/dispatch [::event/update-entity-field ctx decision-id :decision/notes v]))}]]))
